import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { DialogTopBar } from '../components/DialogTopBar';
import { toast } from 'sonner';
import {
    DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors
} from '@dnd-kit/core';
import {
    arrayMove, SortableContext, useSortable, verticalListSortingStrategy, sortableKeyboardCoordinates
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
    ArrowLeft, Save, Plus, Trash2, GripVertical, ChevronDown, ChevronUp,
    Loader2, FileText, Check, X, Copy, Eye, EyeOff, FileDown, Columns, Settings2
} from 'lucide-react';

const DEFAULT_SCALE = [
    { score: 3, label: 'No evidence' },
    { score: 4, label: 'Way below expectations' },
    { score: 5, label: 'Requires more work' },
    { score: 6, label: 'Meets standard' },
    { score: 7, label: 'Above standard' },
    { score: 8, label: 'Excels' }
];

const DEFAULT_EXTRA_COLUMNS = [
    { id: 'qcto_pm', label: 'QCTO PM' },
    { id: 'highfield_units', label: 'Highfield Units' }
];

// Sortable wrapper for sections
const SortableSection = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            {children({ dragHandleProps: listeners })}
        </div>
    );
};

// Sortable wrapper for criteria within a section
const SortableCriterion = ({ id, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
    return (
        <div ref={setNodeRef} style={style} {...attributes}>
            {children({ dragHandleProps: listeners })}
        </div>
    );
};

const COMPONENT_LABELS = {
    header: 'Header & Student Info',
    scale: 'Assessment Scale',
    sections: 'Assessment Sections',
    results: 'Result Sections',
    feedback: 'Feedback Areas',
    signatures: 'Signatures'
};

// Sortable component block - MUST be outside the main component to avoid focus loss on re-render
const SortableComponentBlock = ({ id: blockId, children }) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: blockId });
    const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
    return (
        <div ref={setNodeRef} style={style} {...attributes} className="group">
            <div className="flex items-center gap-2 mb-1">
                <button {...listeners} className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 p-1" data-testid={`drag-handle-${blockId}`}>
                    <GripVertical className="w-4 h-4" />
                </button>
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wide">{COMPONENT_LABELS[blockId]}</span>
            </div>
            {children}
        </div>
    );
};

const FormBuilderPage = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { getAuthHeaders, API } = useAuth();

    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [previewMode, setPreviewMode] = useState(false);
    const [exporting, setExporting] = useState(false);
    const previewRef = useRef(null);

    // Form data
    const [formName, setFormName] = useState('');
    const [description, setDescription] = useState('');
    const [headerFields, setHeaderFields] = useState({ unitElo: '', qcto: '', pm: '', highfield: '', cathsseta: '' });
    const [assessmentScale, setAssessmentScale] = useState(DEFAULT_SCALE);
    const [sections, setSections] = useState([]);
    const [extraColumns, setExtraColumns] = useState(DEFAULT_EXTRA_COLUMNS);
    const [resultSections, setResultSections] = useState({ kitchenAssessment: true, tastingAssessment: true, combinedResult: true });
    const [feedbackSections, setFeedbackSections] = useState({ assessorFeedback: true, studentFeedback: true, actionPlan: true });
    const [signatureFields, setSignatureFields] = useState(['student', 'assessor', 'internalVerifier']);

    // Component blocks ordering
    const [componentOrder, setComponentOrder] = useState([
        'header', 'scale', 'sections', 'results', 'feedback', 'signatures'
    ]);

    // UI state
    const [expandedSections, setExpandedSections] = useState({});
    const [addSectionModal, setAddSectionModal] = useState(false);
    const [newSectionTitle, setNewSectionTitle] = useState('');
    const [addCriterionModal, setAddCriterionModal] = useState(null);
    const [newCriterionLabel, setNewCriterionLabel] = useState('');
    const [newCriterionGuidance, setNewCriterionGuidance] = useState('');
    const [columnsModal, setColumnsModal] = useState(false);
    const [newColLabel, setNewColLabel] = useState('');

    // DnD sensors
    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const fetchForm = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/admin/assessment-forms/${id}`, { headers: getAuthHeaders() });
            const f = res.data;
            setForm(f);
            setFormName(f.form_name || '');
            const fd = f.form_data || {};
            setDescription(fd.description || '');
            if (fd.headerFields) setHeaderFields(fd.headerFields);
            if (fd.assessmentScale?.length > 0) setAssessmentScale(fd.assessmentScale);
            if (fd.extraColumns?.length > 0) setExtraColumns(fd.extraColumns);
            if (fd.componentOrder?.length > 0) setComponentOrder(fd.componentOrder);
            if (fd.sections?.length > 0) {
                setSections(fd.sections.map(s => {
                    // Merge section-level AI-extracted references into extraColumnValues
                    const sectionMerged = { ...(s.extraColumnValues || {}) };
                    if (s.references) {
                        if (s.references.qcto?.length && sectionMerged.qcto_pm === undefined) sectionMerged.qcto_pm = s.references.qcto.join(', ');
                        if (s.references.highfield?.length && sectionMerged.highfield_units === undefined) sectionMerged.highfield_units = s.references.highfield.join(', ');
                    }
                    return {
                        ...s,
                        id: s.id || crypto.randomUUID(),
                        extraColumnValues: sectionMerged,
                        criteria: (s.criteria || []).map(c => {
                            // Merge AI-extracted references into extraColumnValues for editing
                            const merged = { ...(c.extraColumnValues || {}) };
                            if (c.references) {
                                if (c.references.qcto && merged.qcto_pm === undefined) merged.qcto_pm = c.references.qcto.join(', ');
                                if (c.references.highfield && merged.highfield_units === undefined) merged.highfield_units = c.references.highfield.join(', ');
                            }
                            return {
                                ...c,
                                id: c.id || crypto.randomUUID(),
                                scale: c.scale || [3, 4, 5, 6, 7, 8],
                                extraColumnValues: merged
                            };
                        })
                    };
                }));
                const expanded = {};
                fd.sections.forEach(s => { expanded[s.id || ''] = true; });
                setExpandedSections(expanded);
            }
            if (fd.resultSections) setResultSections(fd.resultSections);
            if (fd.feedbackSections) setFeedbackSections(fd.feedbackSections);
            if (fd.signatureFields) setSignatureFields(fd.signatureFields);
        } catch (e) {
            toast.error('Failed to load form');
            navigate('/assessment-forms');
        } finally { setLoading(false); }
    }, [id, API, getAuthHeaders, navigate]);

    useEffect(() => { fetchForm(); }, [fetchForm]);

    // Section ops
    const addSection = () => {
        if (!newSectionTitle.trim()) return;
        const s = { id: crypto.randomUUID(), title: newSectionTitle.trim(), criteria: [] };
        setSections(prev => [...prev, s]);
        setExpandedSections(prev => ({ ...prev, [s.id]: true }));
        setNewSectionTitle('');
        setAddSectionModal(false);
    };

    const updateSectionTitle = (sid, title) => setSections(prev => prev.map(s => s.id === sid ? { ...s, title } : s));
    const removeSection = (sid) => setSections(prev => prev.filter(s => s.id !== sid));

    const duplicateSection = (sid) => {
        setSections(prev => {
            const idx = prev.findIndex(s => s.id === sid);
            if (idx === -1) return prev;
            const orig = prev[idx];
            const copy = { ...orig, id: crypto.randomUUID(), title: `${orig.title} (Copy)`, criteria: orig.criteria.map(c => ({ ...c, id: crypto.randomUUID() })) };
            const arr = [...prev]; arr.splice(idx + 1, 0, copy); return arr;
        });
    };

    // Criterion ops
    const addCriterion = (sid) => {
        if (!newCriterionLabel.trim()) return;
        const c = { id: crypto.randomUUID(), label: newCriterionLabel.trim(), guidance: newCriterionGuidance.trim(), scale: [3, 4, 5, 6, 7, 8], extraColumnValues: {} };
        setSections(prev => prev.map(s => s.id === sid ? { ...s, criteria: [...s.criteria, c] } : s));
        setNewCriterionLabel(''); setNewCriterionGuidance(''); setAddCriterionModal(null);
    };

    const updateCriterion = (sid, cid, field, value) => {
        setSections(prev => prev.map(s => s.id === sid ? { ...s, criteria: s.criteria.map(c => c.id === cid ? { ...c, [field]: value } : c) } : s));
    };

    const updateCriterionExtra = (sid, cid, colId, value) => {
        setSections(prev => prev.map(s => s.id === sid ? {
            ...s, criteria: s.criteria.map(c => c.id === cid ? { ...c, extraColumnValues: { ...c.extraColumnValues, [colId]: value } } : c)
        } : s));
    };

    const updateSectionExtra = (sid, colId, value) => {
        setSections(prev => prev.map(s => s.id === sid ? {
            ...s, extraColumnValues: { ...(s.extraColumnValues || {}), [colId]: value }
        } : s));
    };

    const removeCriterion = (sid, cid) => setSections(prev => prev.map(s => s.id === sid ? { ...s, criteria: s.criteria.filter(c => c.id !== cid) } : s));

    // DnD handlers
    const handleSectionDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setSections(prev => {
            const oldIdx = prev.findIndex(s => s.id === active.id);
            const newIdx = prev.findIndex(s => s.id === over.id);
            return arrayMove(prev, oldIdx, newIdx);
        });
    };

    const handleCriteriaDragEnd = (sectionId) => (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setSections(prev => prev.map(s => {
            if (s.id !== sectionId) return s;
            const oldIdx = s.criteria.findIndex(c => c.id === active.id);
            const newIdx = s.criteria.findIndex(c => c.id === over.id);
            return { ...s, criteria: arrayMove(s.criteria, oldIdx, newIdx) };
        }));
    };

    const handleComponentDragEnd = (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        setComponentOrder(prev => {
            const oldIdx = prev.indexOf(active.id);
            const newIdx = prev.indexOf(over.id);
            return arrayMove(prev, oldIdx, newIdx);
        });
    };

    // Extra columns
    const addExtraColumn = () => {
        if (!newColLabel.trim()) return;
        setExtraColumns(prev => [...prev, { id: crypto.randomUUID(), label: newColLabel.trim() }]);
        setNewColLabel('');
    };
    const removeExtraColumn = (colId) => setExtraColumns(prev => prev.filter(c => c.id !== colId));

    // Save
    const handleSave = async () => {
        setSaving(true);
        try {
            const formData = {
                description, headerFields, assessmentScale, sections, extraColumns,
                componentOrder, resultSections, feedbackSections, signatureFields
            };
            await axios.put(`${API}/admin/assessment-forms/${id}`, { form_name: formName, form_data: formData }, { headers: getAuthHeaders() });
            toast.success('Form saved');
        } catch (e) { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    const exportPreviewPDF = async () => {
        if (!previewRef.current) return;
        setExporting(true);
        try {
            await html2pdf().set({
                margin: [8, 5, 8, 5],
                filename: `${(formName || 'assessment_form').replace(/\s+/g, '_')}.pdf`,
                image: { type: 'jpeg', quality: 0.98 },
                html2canvas: { scale: 2, useCORS: true },
                jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
                pagebreak: { mode: ['avoid-all', 'css', 'legacy'] }
            }).from(previewRef.current).save();
            toast.success('PDF exported');
        } catch (e) { toast.error('Export failed'); }
        finally { setExporting(false); }
    };

    const totalCriteria = sections.reduce((sum, s) => sum + s.criteria.length, 0);

    if (loading) return (
        <Layout><div className="flex items-center justify-center h-[60vh]"><Loader2 className="w-10 h-10 animate-spin text-gray-400" /></div></Layout>
    );

    // ===========================
    // LIVE PREVIEW (PDF LAYOUT)
    // ===========================
    const LivePreview = () => {
        const scaleScores = assessmentScale.map(s => s.score);
        return (
            <div ref={previewRef} className="bg-white border border-gray-300 mx-auto max-w-[900px] text-[11px] leading-snug" data-testid="form-live-preview" style={{ fontFamily: 'Arial, sans-serif' }}>
                {/* Title */}
                <div className="border-b-2 border-black p-4 text-center">
                    <h1 className="text-base font-bold uppercase">{formName || 'Untitled Assessment Form'}</h1>
                    {description && <p className="text-[10px] text-gray-600 mt-1">{description}</p>}
                </div>

                {/* Header info fields */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 p-3 border-b border-gray-400 text-[10px]">
                    {headerFields.unitElo && <div><span className="font-bold">Unit / ELO / KM:</span> {headerFields.unitElo}</div>}
                    {headerFields.qcto && <div><span className="font-bold">QCTO:</span> {headerFields.qcto}</div>}
                    {headerFields.pm && <div><span className="font-bold">PM:</span> {headerFields.pm}</div>}
                    {headerFields.highfield && <div><span className="font-bold">Highfield Units:</span> {headerFields.highfield}</div>}
                    {headerFields.cathsseta && <div><span className="font-bold">CATHSSETA:</span> {headerFields.cathsseta}</div>}
                </div>

                {/* Student info row */}
                <div className="grid grid-cols-2 gap-x-6 gap-y-1 p-3 border-b-2 border-black text-[10px]">
                    <div><span className="font-bold">Student Name:</span> ___________________</div>
                    <div><span className="font-bold">Student Number:</span> ___________________</div>
                    <div><span className="font-bold">Date:</span> ___________________</div>
                    <div><span className="font-bold">Assessor:</span> ___________________</div>
                    <div><span className="font-bold">Lecturer:</span> ___________________</div>
                    <div><span className="font-bold">Year of Study:</span> ___________________</div>
                </div>

                {/* Scale legend */}
                <div className="p-2 border-b border-gray-400 bg-gray-50">
                    <span className="font-bold mr-2">Scale:</span>
                    {assessmentScale.map(s => (
                        <span key={s.score} className="mr-3"><span className="font-bold">{s.score}</span> = {s.label}</span>
                    ))}
                </div>

                {/* Column header row */}
                <table className="w-full border-collapse">
                    <thead>
                        <tr className="border-b-2 border-black bg-gray-100">
                            <th className="text-left px-2 py-2 border border-gray-400 font-bold w-[35%]">Task</th>
                            <th className="text-center px-0 py-2 border border-gray-400 font-bold" colSpan={scaleScores.length}>Evaluation</th>
                            <th className="text-left px-2 py-2 border border-gray-400 font-bold w-[30%]">Comments</th>
                            {extraColumns.map(col => (
                                <th key={col.id} className="text-center px-1 py-2 border border-gray-400 font-bold text-[9px] w-[8%]">{col.label}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {sections.map(section => {
                            const criteriaRows = section.criteria || [];
                            return (
                                <React.Fragment key={section.id}>
                                    {/* Section header */}
                                    <tr className="bg-[#1e3a5f]">
                                        <td className="px-2 py-2 text-white font-bold border border-gray-600">{section.title}</td>
                                        {scaleScores.map(s => (
                                            <td key={s} className="text-center text-white font-bold border border-gray-600 px-0.5">{s}</td>
                                        ))}
                                        {/* Comments - merged, white bg */}
                                        <td className="border border-gray-400 bg-white" rowSpan={criteriaRows.length + 1}>
                                            <div className="px-2 py-1 text-[10px] text-gray-400 italic min-h-[40px]">Comments...</div>
                                        </td>
                                        {/* Extra columns - per section, merged */}
                                        {extraColumns.map(col => (
                                            <td key={col.id} className="border border-gray-400 bg-white text-center text-[9px] text-gray-800 align-top px-1 py-1.5" rowSpan={criteriaRows.length + 1}>
                                                {section.extraColumnValues?.[col.id] || ''}
                                            </td>
                                        ))}
                                    </tr>
                                    {/* Criteria rows */}
                                    {criteriaRows.map((c, idx) => (
                                        <tr key={c.id} className={idx % 2 === 1 ? 'bg-gray-50' : ''}>
                                            <td className="px-2 py-1.5 border border-gray-300 align-top">
                                                <div className="text-[11px]">{c.label}</div>
                                                {c.guidance && <div className="text-[9px] text-gray-500 italic">{c.guidance}</div>}
                                            </td>
                                            {scaleScores.map(s => (
                                                <td key={s} className="text-center border border-gray-300 px-0 py-1.5 w-[4%]">
                                                    <div className="w-4 h-4 border border-gray-400 mx-auto" />
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                    {criteriaRows.length === 0 && (
                                        <tr><td colSpan={scaleScores.length + 1} className="px-2 py-3 text-center text-gray-400 italic border border-gray-300">No criteria</td></tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>

                {sections.length === 0 && <div className="p-6 text-center text-gray-400 italic">No sections defined</div>}

                {/* Results */}
                {(resultSections.kitchenAssessment || resultSections.tastingAssessment || resultSections.combinedResult) && (
                    <div className="border-t-2 border-black p-3">
                        <p className="font-bold text-[10px] uppercase mb-2">Results</p>
                        <div className="grid grid-cols-3 gap-3">
                            {resultSections.kitchenAssessment && <div className="border border-gray-400 p-2 text-center"><p className="font-bold text-[10px]">Kitchen Assessment</p><div className="h-5 border-b border-dotted border-gray-400 mt-1" /></div>}
                            {resultSections.tastingAssessment && <div className="border border-gray-400 p-2 text-center"><p className="font-bold text-[10px]">Tasting Assessment</p><div className="h-5 border-b border-dotted border-gray-400 mt-1" /></div>}
                            {resultSections.combinedResult && <div className="border border-gray-400 p-2 text-center"><p className="font-bold text-[10px]">Combined Result</p><div className="h-5 border-b border-dotted border-gray-400 mt-1" /></div>}
                        </div>
                    </div>
                )}

                {/* Feedback */}
                {(feedbackSections.assessorFeedback || feedbackSections.studentFeedback || feedbackSections.actionPlan) && (
                    <div className="border-t border-gray-400 p-3 space-y-2">
                        {feedbackSections.assessorFeedback && <div><p className="font-bold text-[10px]">Assessor Feedback:</p><div className="border border-gray-300 h-12 mt-1" /></div>}
                        {feedbackSections.studentFeedback && <div><p className="font-bold text-[10px]">Student Feedback:</p><div className="border border-gray-300 h-12 mt-1" /></div>}
                        {feedbackSections.actionPlan && <div><p className="font-bold text-[10px]">Action Plan:</p><div className="border border-gray-300 h-12 mt-1" /></div>}
                    </div>
                )}

                {/* Signatures */}
                {signatureFields.length > 0 && (
                    <div className="border-t-2 border-black p-3">
                        <div className={`grid gap-6 ${signatureFields.length > 2 ? 'grid-cols-3' : `grid-cols-${signatureFields.length}`}`}>
                            {signatureFields.map(s => (
                                <div key={s} className="text-center">
                                    <div className="border-b-2 border-black h-10 mb-1" />
                                    <p className="text-[10px] font-bold">{s === 'internalVerifier' ? 'Internal Verifier' : s.charAt(0).toUpperCase() + s.slice(1)}</p>
                                    <p className="text-[9px] text-gray-500 mt-0.5">Date: ____________</p>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    // ===========================
    // EDIT MODE RENDER BLOCKS
    // ===========================
    const renderComponentBlock = (blockId) => {
        switch (blockId) {
            case 'header': return (
                <SortableComponentBlock key={blockId} id={blockId}>
                    <div className="bg-white rounded-lg border border-gray-200 p-5 space-y-4">
                        <div>
                            <label className="text-xs text-gray-500 mb-1 block">Description</label>
                            <Textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Assessment description..." rows={2} className="resize-none text-sm" />
                        </div>
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                            {[
                                { key: 'unitElo', label: 'Unit / ELO / KM' }, { key: 'qcto', label: 'QCTO' },
                                { key: 'pm', label: 'PM' }, { key: 'highfield', label: 'Highfield Units' },
                                { key: 'cathsseta', label: 'CATHSSETA' }
                            ].map(f => (
                                <div key={f.key}>
                                    <label className="text-xs text-gray-500 mb-1 block">{f.label}</label>
                                    <Input value={headerFields[f.key] || ''} onChange={e => setHeaderFields(p => ({ ...p, [f.key]: e.target.value }))} className="text-sm h-9" />
                                </div>
                            ))}
                        </div>
                    </div>
                </SortableComponentBlock>
            );
            case 'scale': return (
                <SortableComponentBlock key={blockId} id={blockId}>
                    <div className="bg-white rounded-lg border border-gray-200 p-5">
                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                            {assessmentScale.map((s, idx) => (
                                <div key={s.score} className="border border-gray-100 rounded-lg p-3 text-center">
                                    <div className="w-10 h-10 bg-ica-navy rounded-lg flex items-center justify-center mx-auto mb-2">
                                        <span className="text-white font-bold">{s.score}</span>
                                    </div>
                                    <Input value={s.label} onChange={e => { const a = [...assessmentScale]; a[idx] = { ...a[idx], label: e.target.value }; setAssessmentScale(a); }} className="text-xs text-center h-7" />
                                </div>
                            ))}
                        </div>
                    </div>
                </SortableComponentBlock>
            );
            case 'sections': return (
                <SortableComponentBlock key={blockId} id={blockId}>
                    <div className="space-y-4">
                        <div className="flex items-center gap-2">
                            <Button variant="outline" size="sm" onClick={() => setAddSectionModal(true)} data-testid="add-section-btn">
                                <Plus className="w-4 h-4 mr-1" /> Add Section
                            </Button>
                            <Button variant="outline" size="sm" onClick={() => setColumnsModal(true)} data-testid="manage-columns-btn">
                                <Columns className="w-4 h-4 mr-1" /> Extra Columns ({extraColumns.length})
                            </Button>
                        </div>

                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleSectionDragEnd}>
                            <SortableContext items={sections.map(s => s.id)} strategy={verticalListSortingStrategy}>
                                {sections.map(section => (
                                    <SortableSection key={section.id} id={section.id}>
                                        {({ dragHandleProps }) => (
                                            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden" data-testid={`section-${section.id}`}>
                                                {/* Section header */}
                                                <div className="flex items-center gap-2 px-4 py-3 bg-[#1e3a5f] text-white">
                                                    <button {...dragHandleProps} className="cursor-grab active:cursor-grabbing text-white/50 hover:text-white p-0.5" data-testid={`section-drag-${section.id}`}>
                                                        <GripVertical className="w-4 h-4" />
                                                    </button>
                                                    <Input value={section.title} onChange={e => updateSectionTitle(section.id, e.target.value)}
                                                        className="text-sm font-semibold flex-1 bg-transparent border-0 text-white h-8 shadow-none p-0 placeholder:text-white/50 focus-visible:ring-0" />
                                                    <span className="text-xs text-white/60">{section.criteria.length} criteria</span>
                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-white/50 hover:text-white hover:bg-white/10" onClick={() => duplicateSection(section.id)} title="Duplicate Section">
                                                        <Copy className="w-3.5 h-3.5" />
                                                    </Button>
                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-white/50 hover:text-white hover:bg-white/10" onClick={() => setExpandedSections(p => ({ ...p, [section.id]: !p[section.id] }))} title="Toggle Section">
                                                        {expandedSections[section.id] ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                                                    </Button>
                                                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-300 hover:text-red-100 hover:bg-red-500/20" onClick={() => removeSection(section.id)} title="Remove Section">
                                                        <Trash2 className="w-3.5 h-3.5" />
                                                    </Button>
                                                </div>

                                                {expandedSections[section.id] && (
                                                    <div>
                                                        {/* Section-level extra column values */}
                                                        {extraColumns.length > 0 && (
                                                            <div className="flex items-center gap-3 px-4 py-2.5 bg-blue-50/50 border-b border-gray-100">
                                                                {extraColumns.map(col => (
                                                                    <div key={col.id} className="flex items-center gap-2">
                                                                        <span className="text-xs font-medium text-gray-600 whitespace-nowrap">{col.label}:</span>
                                                                        <Input
                                                                            value={section.extraColumnValues?.[col.id] || ''}
                                                                            onChange={e => updateSectionExtra(section.id, col.id, e.target.value)}
                                                                            className="text-xs h-7 w-[200px]"
                                                                            placeholder={`${col.label} for this section...`}
                                                                            data-testid={`section-extra-${section.id}-${col.id}`}
                                                                        />
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                        {/* Column headers */}
                                                        <div className="grid items-center gap-2 px-4 py-2 bg-gray-50 border-b border-gray-100 text-xs font-medium text-gray-500"
                                                            style={{ gridTemplateColumns: '28px 1fr 140px 60px' }}>
                                                            <span></span>
                                                            <span>Task / Criterion</span>
                                                            <span>Guidance</span>
                                                            <span className="text-right">Actions</span>
                                                        </div>

                                                        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleCriteriaDragEnd(section.id)}>
                                                            <SortableContext items={section.criteria.map(c => c.id)} strategy={verticalListSortingStrategy}>
                                                                {section.criteria.map(c => (
                                                                    <SortableCriterion key={c.id} id={c.id}>
                                                                        {({ dragHandleProps: cdp }) => (
                                                                            <div className="grid items-center gap-2 px-4 py-2 border-b border-gray-50 hover:bg-gray-50/50"
                                                                                style={{ gridTemplateColumns: '28px 1fr 140px 60px' }}
                                                                                data-testid={`criterion-row-${c.id}`}>
                                                                                <button {...cdp} className="cursor-grab active:cursor-grabbing text-gray-300 hover:text-gray-500 p-0.5">
                                                                                    <GripVertical className="w-3.5 h-3.5" />
                                                                                </button>
                                                                                <Input value={c.label} onChange={e => updateCriterion(section.id, c.id, 'label', e.target.value)} className="text-sm h-8" />
                                                                                <Input value={c.guidance || ''} onChange={e => updateCriterion(section.id, c.id, 'guidance', e.target.value)} className="text-xs h-8" placeholder="Guidance..." />
                                                                                <div className="flex items-center justify-end gap-0.5">
                                                                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => removeCriterion(section.id, c.id)} title="Remove Task">
                                                                                        <Trash2 className="w-3 h-3" />
                                                                                    </Button>
                                                                                </div>
                                                                            </div>
                                                                        )}
                                                                    </SortableCriterion>
                                                                ))}
                                                            </SortableContext>
                                                        </DndContext>

                                                        <div className="px-4 py-2">
                                                            <Button variant="ghost" size="sm" onClick={() => { setAddCriterionModal(section.id); setNewCriterionLabel(''); setNewCriterionGuidance(''); }}
                                                                className="text-ica-navy hover:bg-ica-navy/5" data-testid={`add-criterion-${section.id}`}>
                                                                <Plus className="w-3.5 h-3.5 mr-1" /> Add Criterion
                                                            </Button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </SortableSection>
                                ))}
                            </SortableContext>
                        </DndContext>

                        {sections.length === 0 && (
                            <div className="text-center py-10 bg-white rounded-lg border border-gray-200">
                                <FileText className="w-10 h-10 mx-auto mb-2 text-gray-300" />
                                <p className="text-gray-500 text-sm">No sections yet</p>
                                <Button variant="outline" className="mt-3" onClick={() => setAddSectionModal(true)}><Plus className="w-4 h-4 mr-1" /> Add First Section</Button>
                            </div>
                        )}
                    </div>
                </SortableComponentBlock>
            );
            case 'results': return (
                <SortableComponentBlock key={blockId} id={blockId}>
                    <div className="bg-white rounded-lg border border-gray-200 p-5">
                        {[
                            { key: 'kitchenAssessment', label: 'Kitchen Assessment Result' },
                            { key: 'tastingAssessment', label: 'Tasting Assessment' },
                            { key: 'combinedResult', label: 'Combined Assessment Result' }
                        ].map(r => (
                            <label key={r.key} className="flex items-center gap-3 py-2 cursor-pointer">
                                <input type="checkbox" checked={resultSections[r.key] || false} onChange={e => setResultSections(p => ({ ...p, [r.key]: e.target.checked }))} className="rounded border-gray-300 text-ica-gold focus:ring-ica-gold" />
                                <span className="text-sm text-gray-700">{r.label}</span>
                            </label>
                        ))}
                    </div>
                </SortableComponentBlock>
            );
            case 'feedback': return (
                <SortableComponentBlock key={blockId} id={blockId}>
                    <div className="bg-white rounded-lg border border-gray-200 p-5">
                        {[
                            { key: 'assessorFeedback', label: 'Assessor Feedback' },
                            { key: 'studentFeedback', label: 'Student Feedback' },
                            { key: 'actionPlan', label: 'Action Plan' }
                        ].map(f => (
                            <label key={f.key} className="flex items-center gap-3 py-2 cursor-pointer">
                                <input type="checkbox" checked={feedbackSections[f.key] || false} onChange={e => setFeedbackSections(p => ({ ...p, [f.key]: e.target.checked }))} className="rounded border-gray-300 text-ica-gold focus:ring-ica-gold" />
                                <span className="text-sm text-gray-700">{f.label}</span>
                            </label>
                        ))}
                    </div>
                </SortableComponentBlock>
            );
            case 'signatures': return (
                <SortableComponentBlock key={blockId} id={blockId}>
                    <div className="bg-white rounded-lg border border-gray-200 p-5">
                        {['student', 'assessor', 'internalVerifier'].map(s => (
                            <label key={s} className="flex items-center gap-3 py-2 cursor-pointer">
                                <input type="checkbox" checked={signatureFields.includes(s)} onChange={e => {
                                    if (e.target.checked) setSignatureFields(p => [...p, s]);
                                    else setSignatureFields(p => p.filter(x => x !== s));
                                }} className="rounded border-gray-300 text-ica-gold focus:ring-ica-gold" />
                                <span className="text-sm text-gray-700">{s === 'internalVerifier' ? 'Internal Verifier' : s.charAt(0).toUpperCase() + s.slice(1)}</span>
                            </label>
                        ))}
                    </div>
                </SortableComponentBlock>
            );
            default: return null;
        }
    };

    return (
        <Layout>
            <div className="space-y-5 animate-fade-in" data-testid="form-builder-page">
                {/* Page Header */}
                <div className="flex items-start gap-4">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/assessment-forms')} className="mt-1 text-slate-500 hover:text-ica-navy dark:text-slate-400 dark:hover:text-ica-gold" data-testid="back-to-forms" title="Back to Assessment Forms">
                        <ArrowLeft className="w-5 h-5" />
                    </Button>
                    <div className="flex-1">
                        {!previewMode ? (
                            <Input value={formName} onChange={e => setFormName(e.target.value)} className="text-2xl font-bold text-ica-navy border-0 border-b border-transparent hover:border-gray-200 focus:border-ica-gold p-0 h-auto shadow-none rounded-none" data-testid="form-name-input" />
                        ) : (
                            <h1 className="font-heading text-3xl font-bold text-ica-navy">{formName || 'Untitled Form'}</h1>
                        )}
                        <div className="flex items-center gap-3 mt-1">
                            <span className="text-xs px-2 py-0.5 rounded-full bg-ica-navy/10 text-ica-navy font-medium">{sections.length} sections</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-ica-gold/10 text-ica-gold-dark font-medium">{totalCriteria} criteria</span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${form?.status === 'published' ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>{form?.status}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant={previewMode ? "default" : "outline"} onClick={() => setPreviewMode(!previewMode)} data-testid="toggle-preview-btn">
                            {previewMode ? <><EyeOff className="w-4 h-4 mr-2" />Exit Preview</> : <><Eye className="w-4 h-4 mr-2" />Live Preview</>}
                        </Button>
                        {previewMode && (
                            <Button variant="outline" onClick={exportPreviewPDF} disabled={exporting} data-testid="export-preview-pdf-btn">
                                {exporting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Exporting...</> : <><FileDown className="w-4 h-4 mr-2" />Export PDF</>}
                            </Button>
                        )}
                        <Button onClick={handleSave} disabled={saving} className="bg-ica-gold hover:bg-[#a8803a]" data-testid="save-form-btn">
                            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Form</>}
                        </Button>
                    </div>
                </div>

                {previewMode ? (
                    <LivePreview />
                ) : (
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleComponentDragEnd}>
                        <SortableContext items={componentOrder} strategy={verticalListSortingStrategy}>
                            <div className="space-y-5">
                                {componentOrder.map(blockId => renderComponentBlock(blockId))}
                            </div>
                        </SortableContext>
                    </DndContext>
                )}

                {/* Save footer */}
                {!previewMode && (
                    <div className="flex justify-end pb-6">
                        <Button onClick={handleSave} disabled={saving} className="bg-ica-gold hover:bg-[#a8803a] px-8" size="lg">
                            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</> : <><Save className="w-4 h-4 mr-2" />Save Form</>}
                        </Button>
                    </div>
                )}
            </div>

            {/* Add Section Modal */}
            <Dialog open={addSectionModal} onOpenChange={setAddSectionModal}>
                <DialogContent>
                    <DialogTopBar title={<>Add Section</>}>
    <Button size="sm" variant="outline" onClick={() => setAddSectionModal(false)}>Cancel</Button>
                        <Button size="sm" onClick={addSection} data-testid="confirm-add-section">Add Section</Button>
</DialogTopBar>
                    <div className="py-4">
                        <Input value={newSectionTitle} onChange={e => setNewSectionTitle(e.target.value)} placeholder="Section title, e.g. 'Basic Kitchen Procedures'"
                            onKeyDown={e => { if (e.key === 'Enter') addSection(); }} autoFocus data-testid="new-section-input" />
                    </div>
                </DialogContent>
            </Dialog>

            {/* Add Criterion Modal */}
            <Dialog open={!!addCriterionModal} onOpenChange={open => { if (!open) setAddCriterionModal(null); }}>
                <DialogContent>
                    <DialogTopBar title={<>Add Criterion</>}>
    <Button size="sm" variant="outline" onClick={() => setAddCriterionModal(null)}>Cancel</Button>
                        <Button size="sm" onClick={() => addCriterion(addCriterionModal)} data-testid="confirm-add-criterion">Add</Button>
</DialogTopBar>
                    <div className="py-4 space-y-3">
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Task / Criterion *</label>
                            <Input value={newCriterionLabel} onChange={e => setNewCriterionLabel(e.target.value)} placeholder="e.g. 'Select correct equipment for use'"
                                onKeyDown={e => { if (e.key === 'Enter') addCriterion(addCriterionModal); }} autoFocus data-testid="new-criterion-input" />
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-700 mb-1 block">Guidance (optional)</label>
                            <Input value={newCriterionGuidance} onChange={e => setNewCriterionGuidance(e.target.value)} placeholder="Assessor guidance notes..." />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Extra Columns Modal */}
            <Dialog open={columnsModal} onOpenChange={setColumnsModal}>
                <DialogContent className="max-w-sm" data-testid="extra-columns-modal">
                    <DialogHeader><DialogTitle>Extra Columns</DialogTitle></DialogHeader>
                    <p className="text-xs text-gray-500">Add columns like QCTO PM, Highfield Units, etc. These appear in each criterion row.</p>
                    <div className="py-3 space-y-3">
                        <div className="flex gap-2">
                            <Input value={newColLabel} onChange={e => setNewColLabel(e.target.value)} placeholder="Column name..." className="flex-1"
                                onKeyDown={e => { if (e.key === 'Enter') addExtraColumn(); }} data-testid="new-column-input" />
                            <Button onClick={addExtraColumn} data-testid="add-column-btn">Add</Button>
                        </div>
                        <div className="space-y-2">
                            {extraColumns.map(col => (
                                <div key={col.id} className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg">
                                    <span className="text-sm font-medium">{col.label}</span>
                                    <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-red-400 hover:text-red-600" onClick={() => removeExtraColumn(col.id)} title="Remove Column">
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                </div>
                            ))}
                            {extraColumns.length === 0 && <p className="text-sm text-gray-400 text-center py-3">No extra columns</p>}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </Layout>
    );
};

export default FormBuilderPage;
